import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DashboardCard, TableChip } from '../../components/PremiumDesign';
import { Button, ConfirmDeleteModal, SkeletonText } from '@/components';
import { V2Table } from '@/common/components';
import {
  getCRMTasksAndFollowups,
  deleteCRMContactFollowUp,
  pauseCRMContactFollowUp,
  resumeCRMContactFollowUp,
  stopCRMContactFollowUp,
  rescheduleCRMContactFollowUp,
} from '../api';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import {
  getCompanyId,
  formatDate,
  formatDateTime,
  getFollowUpStatusChipStyle,
} from '../utils/tasksAndFollowups';
import { CRMFollowUpModal } from './CRMFollowUpModal';
import { CRMFollowUpDetailModal } from './CRMFollowUpDetailModal';
import { RescheduleModal } from './RescheduleModal';
import { StopFollowUpConfirmModal } from '../../components/StopFollowUpConfirmModal';
import trashIcon from '../../assets/trashIcon.png';
import eyeIcon from '../../assets/eye.png';
import pauseCircleIcon from '../../assets/leads/pauseCircle.png';
import rescheduleIcon from '../../assets/leads/reschedule.png';
import playIcon from '../../assets/leads/play.png';
import pauseButtonIcon from '../../assets/leads/pauseButton.png';
import { parseApiError } from '@/common/utils';

export function RecentFollowUpsTable() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [followUpToDelete, setFollowUpToDelete] = useState(null);
  const [viewingFollowUpItem, setViewingFollowUpItem] = useState(null);
  const [actionPopup, setActionPopup] = useState(null);

  const {
    data,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['crm-recent-follow-ups', companyId],
    queryFn: () =>
      getCRMTasksAndFollowups(companyId, {
        type: 'follow_up',
        page: 1,
        page_size: 5,
      }),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && data !== undefined,
    300,
    500,
  );
  const showSkeleton = isInitialLoading || showDelayedSkeleton;

  const followUps = data?.results || [];

  // Delete Follow-up Mutation
  const deleteFollowUpMutation = useMutation({
    mutationFn: ({ contactId, followUpId }) =>
      deleteCRMContactFollowUp({ companyId, contactId, followUpId }),
    onSuccess: () => {
      toast.success('Follow-up deleted successfully!');
      setFollowUpToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['crm-recent-follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete follow-up.');
    },
  });

  // Pause Follow-up Mutation
  const { mutate: handlePauseFollowUp } = useMutation({
    mutationFn: ({ contactId, followUpId }) =>
      pauseCRMContactFollowUp({ companyId, contactId, followUpId }),
    onSuccess: () => {
      toast.success('Follow-up paused successfully!');
      queryClient.invalidateQueries({ queryKey: ['crm-recent-follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to pause follow-up.');
    },
  });

  // Resume Follow-up Mutation
  const { mutate: handleResumeFollowUp } = useMutation({
    mutationFn: ({ contactId, followUpId }) =>
      resumeCRMContactFollowUp({ companyId, contactId, followUpId }),
    onSuccess: () => {
      toast.success('Follow-up resumed successfully!');
      queryClient.invalidateQueries({ queryKey: ['crm-recent-follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to resume follow-up.');
    },
  });

  // Stop Follow-up Mutation
  const { mutate: handleStopFollowUp, isPending: isStopping } = useMutation({
    mutationFn: ({ contactId, followUpId }) =>
      stopCRMContactFollowUp({ companyId, contactId, followUpId }),
    onSuccess: () => {
      toast.success('Follow-up stopped successfully!');
      setActionPopup(null);
      queryClient.invalidateQueries({ queryKey: ['crm-recent-follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to stop follow-up.');
    },
  });

  // Reschedule Follow-up Mutation
  const { mutate: handleRescheduleFollowUp, isPending: isRescheduling } =
    useMutation({
      mutationFn: ({ contactId, followUpId, payload }) =>
        rescheduleCRMContactFollowUp({
          companyId,
          contactId,
          followUpId,
          payload,
        }),
      onSuccess: () => {
        toast.success('Follow-up rescheduled successfully!');
        setActionPopup(null);
        queryClient.invalidateQueries({ queryKey: ['crm-recent-follow-ups'] });
        queryClient.invalidateQueries({
          queryKey: ['crm-tasks-and-followups'],
        });
      },
      onError: (err) => {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to reschedule follow-up.');
      },
    });

  const handleRowClick = (item) => {
    if (item.contact?.id) {
      const contactId = String(item.contact.id);
      const contactName = item.contact.name || '';
      try {
        sessionStorage.setItem('crm-contact-overview-id', contactId);
        sessionStorage.setItem('crm-contact-overview-name', contactName);
        window.dispatchEvent(new Event('crm-contact-overview-updated'));
      } catch {
        // ignore storage errors
      }
      navigate('/crm-contacts/overview?tab=follow-ups', {
        state: { contactId, contactName, from: '/crm-dashboard' },
      });
    } else {
      navigate('/crm-dashboard/followup');
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'sr',
        label: 'Sr No.',
        defaultWidth: 80,
        minWidth: 64,
        resizable: false,
        skeleton: <SkeletonText width="w-8" height="h-4" />,
        cell: (_row, index) => index + 1,
      },
      {
        id: 'template',
        label: 'Template Name',
        defaultWidth: 200,
        minWidth: 140,
        resizable: true,
        skeleton: <SkeletonText width="w-40" height="h-4" />,
        cell: (item) => {
          const d = item.data;
          return (
            <span className="truncate block font-medium">
              {d?.template_name ||
                d?.template?.name ||
                (d?.message_type === 'message'
                  ? d?.standard_message_body || 'Standard Message'
                  : '-')}
            </span>
          );
        },
      },
      {
        id: 'customer',
        label: 'Customer',
        defaultWidth: 150,
        minWidth: 110,
        resizable: true,
        skeleton: <SkeletonText width="w-28" height="h-4" />,
        cell: (item) => (
          <span className="truncate block font-medium">
            {item.contact?.name || '-'}
          </span>
        ),
      },
      {
        id: 'type',
        label: 'Follow-Up Type',
        defaultWidth: 140,
        minWidth: 110,
        resizable: true,
        skeleton: <SkeletonText width="w-20" height="h-4" />,
        cell: (item) =>
          item.data?.follow_up_type === 'scheduled'
            ? 'Scheduled'
            : item.data?.follow_up_type === 'interval'
              ? 'Interval'
              : item.data?.follow_up_type || '-',
      },
      {
        id: 'message',
        label: 'Message Type',
        defaultWidth: 140,
        minWidth: 110,
        resizable: true,
        skeleton: <SkeletonText width="w-20" height="h-4" />,
        cell: (item) =>
          item.data?.message_type === 'template'
            ? 'Template'
            : item.data?.message_type === 'message'
              ? 'Message'
              : item.data?.message_type || '-',
      },
      {
        id: 'attempts',
        label: 'Attempts',
        defaultWidth: 110,
        minWidth: 80,
        resizable: true,
        skeleton: <SkeletonText width="w-12" height="h-4" />,
        cell: (item) =>
          item.data?.max_attempts
            ? `${item.data?.current_attempts || 0} / ${item.data.max_attempts}`
            : (item.data?.current_attempts ?? 0),
      },
      {
        id: 'next',
        label: 'Next Execution',
        defaultWidth: 160,
        minWidth: 120,
        resizable: true,
        skeleton: <SkeletonText width="w-28" height="h-4" />,
        cell: (item) =>
          item.data?.next_execution_at
            ? formatDateTime(item.data.next_execution_at)
            : item.data?.scheduled_date_time
              ? formatDateTime(item.data.scheduled_date_time)
              : '-',
      },
      {
        id: 'scheduled',
        label: 'Scheduled For',
        defaultWidth: 160,
        minWidth: 120,
        resizable: true,
        skeleton: <SkeletonText width="w-28" height="h-4" />,
        cell: (item) => {
          const d = item.data;
          return d?.scheduled_date_time
            ? formatDateTime(d.scheduled_date_time)
            : d?.scheduled_for
              ? formatDateTime(d.scheduled_for)
              : d?.created_at
                ? formatDate(d.created_at)
                : '-';
        },
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 130,
        minWidth: 100,
        resizable: true,
        skeleton: (
          <SkeletonText width="w-20" height="h-6" className="rounded-full" />
        ),
        cell: (item) => {
          const status = item.data?.status || 'active';
          const label = status.charAt(0).toUpperCase() + status.slice(1);
          return (
            <TableChip {...getFollowUpStatusChipStyle(status)}>
              {label}
            </TableChip>
          );
        },
      },
      {
        id: 'action',
        label: 'Action',
        defaultWidth: 150,
        minWidth: 120,
        resizable: false,
        align: 'center',
        stopPropagation: true,
        skeleton: (
          <SkeletonText width="w-24" height="h-8" className="ml-auto" />
        ),
        cell: (item) => {
          const followUpObj = item.data;
          const s = String(followUpObj?.status || 'active')
            .toLowerCase()
            .trim();
          return (
            <div className="flex items-center justify-end gap-1.5">
              {s === 'scheduled' && (
                <button
                  type="button"
                  disabled={isStopping}
                  onClick={() =>
                    setActionPopup({
                      type: 'stop',
                      contactId: item.contact?.id,
                      followUpId: followUpObj?.id,
                    })
                  }
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-yellow-500/10 focus:outline-none shrink-0 cursor-pointer disabled:opacity-50"
                  title="Stop Follow-up"
                >
                  <img
                    src={pauseCircleIcon}
                    alt="Stop"
                    className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                  />
                </button>
              )}
              {s === 'stopped' && (
                <button
                  type="button"
                  disabled={isRescheduling}
                  onClick={() =>
                    setActionPopup({
                      type: 'reschedule',
                      contactId: item.contact?.id,
                      followUpId: followUpObj?.id,
                    })
                  }
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-purple-500/10 focus:outline-none shrink-0 cursor-pointer disabled:opacity-50"
                  title="Reschedule Follow-up"
                >
                  <img
                    src={rescheduleIcon}
                    alt="Reschedule"
                    className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                  />
                </button>
              )}
              {s === 'paused' && (
                <button
                  type="button"
                  onClick={() => {
                    if (item.contact?.id && followUpObj?.id)
                      handleResumeFollowUp({
                        contactId: item.contact.id,
                        followUpId: followUpObj.id,
                      });
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-green-500/10 focus:outline-none shrink-0 cursor-pointer"
                  title="Start Follow-up"
                >
                  <img
                    src={playIcon}
                    alt="Start"
                    className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                  />
                </button>
              )}
              {(s === 'active' || s === 'running') && (
                <button
                  type="button"
                  onClick={() => {
                    if (item.contact?.id && followUpObj?.id)
                      handlePauseFollowUp({
                        contactId: item.contact.id,
                        followUpId: followUpObj.id,
                      });
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-green-500/10 focus:outline-none shrink-0 cursor-pointer"
                  title="Pause Follow-up"
                >
                  <img
                    src={pauseButtonIcon}
                    alt="Pause"
                    className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                  />
                </button>
              )}
              <button
                type="button"
                onClick={() => setViewingFollowUpItem(item)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue-500/10 focus:outline-none shrink-0 cursor-pointer"
                title="View follow-up"
              >
                <img
                  src={eyeIcon}
                  alt="View"
                  className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                />
              </button>
              <button
                type="button"
                onClick={() => setFollowUpToDelete(item)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#FCEAEC] focus:outline-none shrink-0 cursor-pointer"
                title="Delete follow-up"
              >
                <img
                  src={trashIcon}
                  alt="Delete"
                  className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                />
              </button>
            </div>
          );
        },
      },
    ],
    [isStopping, isRescheduling],
  );

  if (isError && !data) {
    return (
      <DashboardCard className="border border-border-default flex flex-col min-h-0 relative">
        <div className="flex items-center justify-between gap-3 mb-[20px]">
          <h3 className="text-[18px] leading-7 font-semibold text-[#1F2241]">
            Recent Follow-ups
          </h3>
          <Button
            variant="primary"
            className="shrink-0"
            onClick={() => navigate('/crm-dashboard/followup')}
          >
            View all
            <span aria-hidden>→</span>
          </Button>
        </div>
        <div className="w-full p-8 text-center bg-white rounded-xl border border-red-200 flex flex-col items-center justify-center gap-3">
          <p className="text-sm font-medium text-red-600">
            Failed to load recent follow-ups.
          </p>
          <Button variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard className="border border-border-default flex flex-col min-h-0 relative">
      <div className="flex items-center justify-between gap-3 mb-[20px]">
        <h3 className="text-[18px] leading-7 font-semibold text-[#1F2241]">
          Recent Follow-ups
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            className="shrink-0 h-9"
            onClick={() => navigate('/crm-dashboard/followup')}
          >
            View all
            <span aria-hidden>→</span>
          </Button>
        </div>
      </div>

      <V2Table
        bare
        compact
        columns={columns}
        data={followUps.slice(0, 5)}
        rowKey={(row, index) => row.data?.id || `followup-${index}`}
        storageKey="crm_recent_followups_column_widths"
        loading={showSkeleton || (!data && !isError)}
        loadingRowCount={5}
        empty="No follow-ups found"
        onRowClick={handleRowClick}
      />

      {/* Follow-up Add Modal */}
      <CRMFollowUpModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        companyId={companyId}
        onSuccess={() => refetch()}
      />

      {/* Confirm Delete Follow-up Modal */}
      <ConfirmDeleteModal
        isOpen={!!followUpToDelete}
        onCancel={() => setFollowUpToDelete(null)}
        onConfirm={() => {
          if (followUpToDelete?.contact?.id && followUpToDelete?.data?.id) {
            deleteFollowUpMutation.mutate({
              contactId: followUpToDelete.contact.id,
              followUpId: followUpToDelete.data.id,
            });
          }
        }}
        title="Delete Follow-up"
        description="Are you sure you want to delete this follow-up? This action cannot be undone."
        itemName="this follow-up"
        isPending={deleteFollowUpMutation.isPending}
      />

      {/* Detail View Modal for Follow-up */}
      <CRMFollowUpDetailModal
        companyId={companyId}
        contactId={viewingFollowUpItem?.contact?.id}
        followUpId={viewingFollowUpItem?.data?.id}
        onClose={() => setViewingFollowUpItem(null)}
        isStopping={isStopping}
        onStop={() =>
          setActionPopup({
            type: 'stop',
            contactId: viewingFollowUpItem?.contact?.id,
            followUpId: viewingFollowUpItem?.data?.id,
          })
        }
        onReschedule={() =>
          setActionPopup({
            type: 'reschedule',
            contactId: viewingFollowUpItem?.contact?.id,
            followUpId: viewingFollowUpItem?.data?.id,
          })
        }
      />

      {/* Stop Follow-Up Confirm Modal */}
      <StopFollowUpConfirmModal
        isOpen={actionPopup?.type === 'stop'}
        onCancel={() => {
          if (!isStopping) setActionPopup(null);
        }}
        onConfirm={() =>
          handleStopFollowUp({
            contactId: actionPopup?.contactId,
            followUpId: actionPopup?.followUpId,
          })
        }
        isPending={isStopping}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={actionPopup?.type === 'reschedule'}
        onClose={() => {
          if (!isRescheduling) setActionPopup(null);
        }}
        onReschedule={(newDate) =>
          handleRescheduleFollowUp({
            contactId: actionPopup?.contactId,
            followUpId: actionPopup?.followUpId,
            payload: { new_date_time: newDate },
          })
        }
        isRescheduling={isRescheduling}
      />
    </DashboardCard>
  );
}
